import { FetchFunnelConfigurationResponse } from '~/services/fetch-funnel-configuration';
import { SolarProjectDetails } from '~/services/fetch-funnel-configuration/perch-project-details';
import { resolvePerchProgramId } from '~/utils/resolve-perch-program-id';
import { InfoLabel, InfoRow, InfoSection, InfoSectionTitle, InfoValue } from './dev-tools.style';

interface PerchDevContext {
  queryParams?: {
    programId?: string;
    partnerId?: string;
    perchSfProductId?: string;
    unparsedQueryParams?: {
      perchSfProductId?: string;
      programId?: string;
      partnerId?: string;
    };
  };
  funnelConfiguration?: FetchFunnelConfigurationResponse;
}

type PerchScenario = 'programId' | 'partnerId' | 'perchSfProductId';

interface PerchEnergyDevInfoProps {
  context: unknown;
}

/**
 * Detects how the funnel was configured: program (URL or Demo Tools `PERCH_PROGRAM_ID`), partner, or product.
 * Program is checked before product so a stale `perchSfProductId` in the URL does not hide an active `programId`.
 */
const resolveScenario = (ctx: PerchDevContext): PerchScenario => {
  if (resolvePerchProgramId(ctx.queryParams)) {
    return 'programId';
  }
  const raw = ctx.queryParams?.unparsedQueryParams;
  if (raw?.partnerId || ctx.queryParams?.partnerId) return 'partnerId';
  if (raw?.perchSfProductId) return 'perchSfProductId';
  return 'perchSfProductId';
};

const formatBoolean = (value: boolean | undefined): string => {
  if (value === undefined) return '—';
  return value ? 'Yes' : 'No';
};

const formatValue = (value: string | number | boolean | null | undefined): string => {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'boolean') return formatBoolean(value);
  return String(value);
};

interface InfoFieldProps {
  label: string;
  value: string | number | boolean | null | undefined;
}

const InfoField = ({ label, value }: InfoFieldProps) => (
  <InfoRow>
    <InfoLabel>{label}:&nbsp;</InfoLabel>
    <InfoValue>{formatValue(value)}</InfoValue>
  </InfoRow>
);

interface ProjectDetailsFieldsProps {
  details: SolarProjectDetails | undefined;
}

const ProjectDetailsFields = ({ details }: ProjectDetailsFieldsProps) => (
  <>
    <InfoField label="Program Name" value={details?.programName} />
    <InfoField label="Project SSS ID" value={details?.sssId} />
    <InfoField label="Project Name" value={details?.name} />
    <InfoField label="defaultProductId" value={details?.defaultProductId} />
    <InfoField label="lmiProductId" value={details?.lmiProductId} />
    <InfoField label="CS Capacity Available" value={details?.smallCsCapacityAvailable} />
    <InfoField label="LMI Capacity Available" value={details?.lmiCapacityAvailable} />
    <InfoField label="Resi Capacity Available" value={details?.resiCapacityAvailable} />
    <InfoField label="CI Capacity Available" value={details?.ciCapacityAvailable} />
    <InfoField label="ficoUnderwriting" value={details?.ficoUnderwriting} />
    <InfoField label="suppressFicoCheckForLmi" value={details?.suppressFicoCheckForLmi} />
    <InfoField label="IRA" value={details?.ira} />
    <InfoField label="NMC Discount %" value={details?.nmcDiscountPercent} />
    <InfoField label="LMI NMC Discount %" value={details?.lmiProductDiscountPercent} />
    <InfoField label="Estimated COD Date" value={details?.estimatedCodDateManual} />
    <InfoField label="Initial Service Provider" value={details?.initialServiceProvider} />
    <InfoField label="Size Ratio" value={details?.sizeRatio} />
    <InfoField label="Conversion Ratio" value={details?.conversionRatio} />
  </>
);

interface ProgramIdInfoProps {
  programId: string;
  config: FetchFunnelConfigurationResponse | undefined;
  details: SolarProjectDetails | undefined;
}

const ProgramIdInfo = ({ programId, config, details }: ProgramIdInfoProps) => (
  <InfoSection>
    <InfoSectionTitle>Program Config</InfoSectionTitle>
    <InfoField label="programId" value={programId} />
    <ProjectDetailsFields details={details} />
    <InfoField label="Savings %" value={config?.savingsPercentage} />
    <InfoField label="paymentProvider" value={config?.paymentProvider} />
  </InfoSection>
);

interface ProductIdInfoProps {
  config: FetchFunnelConfigurationResponse | undefined;
  details: SolarProjectDetails | undefined;
}

const ProductIdInfo = ({ config, details }: ProductIdInfoProps) => (
  <InfoSection>
    <InfoSectionTitle>Product Config</InfoSectionTitle>
    <InfoField label="perchSfProductId" value={config?.perchSfProductId} />
    <InfoField label="Savings %" value={config?.savingsPercentage} />
    <InfoField
      label="Supported Customer Types"
      value={config?.supportedCustomerTypes?.join(', ')}
    />
    <InfoField label="Proof Doc Requirement" value={config?.proofDocRequirement} />
    <InfoField label="paymentProvider" value={config?.paymentProvider} />
    <ProjectDetailsFields details={details} />
  </InfoSection>
);

interface PartnerIdInfoProps {
  partnerId: string;
  config: FetchFunnelConfigurationResponse | undefined;
  details: SolarProjectDetails | undefined;
}

const PartnerIdInfo = ({ partnerId, config, details }: PartnerIdInfoProps) => (
  <InfoSection>
    <InfoSectionTitle>Partner Config</InfoSectionTitle>
    <InfoField label="partnerId" value={partnerId} />
    <InfoField label="perchSfProductId" value={config?.perchSfProductId} />
    <InfoField label="Savings %" value={config?.savingsPercentage} />
    <InfoField
      label="Supported Customer Types"
      value={config?.supportedCustomerTypes?.join(', ')}
    />
    <InfoField label="paymentProvider" value={config?.paymentProvider} />
    <ProjectDetailsFields details={details} />
  </InfoSection>
);

export const PerchEnergyDevInfo = ({ context }: PerchEnergyDevInfoProps) => {
  const ctx = context as PerchDevContext;
  const scenario = resolveScenario(ctx);
  const config = ctx.funnelConfiguration;
  const details = config?.projectDetails;

  if (scenario === 'programId') {
    const programId = resolvePerchProgramId(ctx.queryParams)!;
    return <ProgramIdInfo programId={programId} config={config} details={details} />;
  }

  if (scenario === 'partnerId') {
    const partnerId = ctx.queryParams?.partnerId ?? ctx.queryParams?.unparsedQueryParams?.partnerId;
    return <PartnerIdInfo partnerId={partnerId ?? ''} config={config} details={details} />;
  }

  return <ProductIdInfo config={config} details={details} />;
};
